<!-- src/components/AdminPanel.vue -->
<template>
  <div class="min-h-screen bg-gradient-to-br from-purple-600 via-blue-600 to-purple-800 text-white">
    <!-- Header -->
    <div class="bg-black/10 backdrop-blur-md px-8 py-6 flex flex-col md:flex-row justify-between items-center gap-4">
      <h1 class="text-2xl md:text-3xl font-light m-0">Pannello Amministrazione</h1>
      <router-link 
        to="/monitor" 
        class="px-6 py-3 bg-white/20 border border-white/30 rounded-lg transition-all duration-300 hover:bg-white/30 hover:-translate-y-0.5 text-white no-underline"
      >
        🖥️ Vai al Monitor
      </router-link>
    </div>

    <!-- Content -->
    <div class="p-4 md:p-8 flex flex-col gap-8 max-w-4xl mx-auto">
      
      <!-- Sezione Import Dati -->
      <div class="bg-white/10 backdrop-blur-md rounded-xl p-6 md:p-8 border border-white/20">
        <h2 class="text-xl md:text-2xl font-medium mb-4">Import Dati SOAP</h2>
        <p class="opacity-90 leading-relaxed mb-6">
          Importa i dati di pianificazione dal servizio SOAP per un periodo specifico.
        </p>

        <!-- Cambiato da form a div per evitare il riconoscimento come form di login -->
        <div class="flex flex-col gap-4">
          <div class="flex flex-col gap-2">
            <label for="username" class="font-medium text-sm">Username:</label>
            <input
              id="username"
              v-model="credentials.username"
              type="text"
              required
              placeholder="Nome utente SOAP"
              autocomplete="new-password"
              data-form-type="other"
              class="px-3 py-2.5 border border-white/30 rounded-md bg-white/10 text-white placeholder-white/60 focus:outline-none focus:border-white/60 focus:bg-white/15"
            />
          </div>

          <div class="flex flex-col gap-2">
            <label for="password" class="font-medium text-sm">Password:</label>
            <input
              id="password"
              v-model="credentials.password"
              type="text"
              required
              placeholder="Password SOAP"
              autocomplete="new-password"
              data-form-type="other"
              style="-webkit-text-security: disc; text-security: disc;"
              class="px-3 py-2.5 border border-white/30 rounded-md bg-white/10 text-white placeholder-white/60 focus:outline-none focus:border-white/60 focus:bg-white/15"
            />
          </div>

          <div class="flex flex-col gap-2">
            <label for="company" class="font-medium text-sm">Company:</label>
            <input
              id="company"
              v-model="credentials.company"
              type="text"
              required
              placeholder="Codice azienda (es. 001)"
              maxlength="3"
              autocomplete="off"
              class="px-3 py-2.5 border border-white/30 rounded-md bg-white/10 text-white placeholder-white/60 focus:outline-none focus:border-white/60 focus:bg-white/15"
            />
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="flex flex-col gap-2">
              <label for="dataInizio" class="font-medium text-sm">Data Inizio:</label>
              <input
                id="dataInizio"
                v-model="dateRange.dataInizio"
                type="date"
                required
                class="px-3 py-2.5 border border-white/30 rounded-md bg-white/10 text-white focus:outline-none focus:border-white/60 focus:bg-white/15"
              />
            </div>

            <div class="flex flex-col gap-2">
              <label for="dataFine" class="font-medium text-sm">Data Fine:</label>
              <input
                id="dataFine"
                v-model="dateRange.dataFine"
                type="date"
                required
                class="px-3 py-2.5 border border-white/30 rounded-md bg-white/10 text-white focus:outline-none focus:border-white/60 focus:bg-white/15"
              />
            </div>
          </div>

          <div class="flex flex-col md:flex-row gap-4 mt-4">
            <button 
              type="button"
              @click="importData"
              class="px-6 py-3 bg-green-500 text-white rounded-md font-medium transition-all duration-300 hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="importing"
            >
              <span v-if="importing">⟳ Importando...</span>
              <span v-else>📥 Importa Dati</span>
            </button>

            <button 
              type="button" 
              @click="setCurrentWeek"
              class="px-6 py-3 bg-white/20 border border-white/30 text-white rounded-md transition-all duration-300 hover:bg-white/30 disabled:opacity-50"
              :disabled="importing"
            >
              📅 Settimana Corrente
            </button>
          </div>
        </div>

        <!-- Progress indicator -->
        <div v-if="importing" class="mt-6 text-center">
          <div class="w-full h-1.5 bg-white/20 rounded-full overflow-hidden mb-2">
            <div class="h-full bg-gradient-to-r from-green-500 to-green-400 animate-pulse"></div>
          </div>
          <p class="text-sm">Import in corso... Questa operazione potrebbe richiedere alcuni minuti.</p>
        </div>

        <!-- Import results -->
        <div v-if="importResult" class="mt-6">
          <div v-if="importResult.success" class="bg-green-500/20 border border-green-500/30 rounded-lg p-4">
            <h3 class="text-lg font-medium mb-2">✅ Import completato con successo!</h3>
            <p class="mb-2">{{ importResult.message }}</p>
            <p><strong>{{ importResult.count }}</strong> record importati.</p>
          </div>
          
          <div v-else class="bg-red-500/20 border border-red-500/30 rounded-lg p-4">
            <h3 class="text-lg font-medium mb-2">❌ Errore durante l'import</h3>
            <p class="mb-3">{{ importResult.message }}</p>
            <button 
              @click="tryAgain" 
              class="px-4 py-2 bg-red-500 text-white rounded-md transition-all duration-300 hover:bg-red-600"
            >
              🔄 Riprova
            </button>
          </div>
        </div>
      </div>

      <!-- Sezione Statistiche -->
      <div class="bg-white/10 backdrop-blur-md rounded-xl p-6 md:p-8 border border-white/20">
        <h2 class="text-xl md:text-2xl font-medium mb-6">Statistiche Database</h2>
        
        <div v-if="loadingStats" class="flex items-center justify-center gap-3 py-4">
          <div class="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
          <span>Caricamento statistiche...</span>
        </div>

        <div v-else-if="stats" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div class="text-center p-4 bg-white/10 border border-white/20 rounded-lg">
            <div class="text-2xl md:text-3xl font-bold mb-2 text-green-400">{{ stats.totalRecords }}</div>
            <div class="text-sm opacity-80">Totale Record</div>
          </div>
          
          <div class="text-center p-4 bg-white/10 border border-white/20 rounded-lg">
            <div class="text-2xl md:text-3xl font-bold mb-2 text-green-400">{{ stats.totalComuni }}</div>
            <div class="text-sm opacity-80">Comuni</div>
          </div>
          
          <div class="text-center p-4 bg-white/10 border border-white/20 rounded-lg">
            <div class="text-2xl md:text-3xl font-bold mb-2 text-green-400">{{ stats.totalOperatori }}</div>
            <div class="text-sm opacity-80">Operatori</div>
          </div>
          
          <div class="text-center p-4 bg-white/10 border border-white/20 rounded-lg">
            <div class="text-2xl md:text-3xl font-bold mb-2 text-green-400">{{ stats.rangeDate }}</div>
            <div class="text-sm opacity-80">Periodo Dati</div>
          </div>
        </div>

        <div class="flex flex-col md:flex-row gap-4 justify-center">
          <button 
            @click="loadStats" 
            class="px-6 py-3 bg-white/20 border border-white/30 text-white rounded-md transition-all duration-300 hover:bg-white/30"
          >
            🔄 Aggiorna Statistiche
          </button>
          
          <button 
            @click="exportData" 
            class="px-6 py-3 bg-blue-500 text-white rounded-md transition-all duration-300 hover:bg-blue-600"
          >
            📊 Esporta CSV
          </button>
        </div>
      </div>

      <!-- Sezione Configurazione -->
      <div class="bg-white/10 backdrop-blur-md rounded-xl p-6 md:p-8 border border-white/20">
        <h2 class="text-xl md:text-2xl font-medium mb-6">Configurazione</h2>
        
        <div class="mb-8 pb-6 border-b border-white/10">
          <h3 class="text-lg mb-4 text-green-400">Impostazioni SOAP</h3>
          
          <div class="flex flex-col gap-2 mb-4">
            <label for="soapUrl" class="font-medium text-sm">URL Servizio SOAP:</label>
            <input
              id="soapUrl"
              v-model="config.soapUrl"
              type="url"
              placeholder="http://your-soap-endpoint.com/service?wsdl"
              autocomplete="off"
              class="px-3 py-2.5 border border-white/30 rounded-md bg-white/10 text-white placeholder-white/60 focus:outline-none focus:border-white/60 focus:bg-white/15"
            />
          </div>
          
          <label class="flex items-center gap-3 cursor-pointer">
            <input 
              v-model="config.saveCredentials" 
              type="checkbox"
              class="w-4 h-4 text-green-500 bg-white/10 border-white/30 rounded focus:ring-green-500 focus:ring-2"
            />
            <span>Salva credenziali localmente (solo per sviluppo)</span>
          </label>
        </div>

        <div class="mb-6">
          <h3 class="text-lg mb-4 text-green-400">Impostazioni Monitor</h3>
          
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="flex flex-col gap-2">
              <label for="refreshInterval" class="font-medium text-sm">Intervallo Auto-refresh (secondi):</label>
              <input
                id="refreshInterval"
                v-model.number="config.refreshInterval"
                type="number"
                min="10"
                max="300"
                class="px-3 py-2.5 border border-white/30 rounded-md bg-white/10 text-white focus:outline-none focus:border-white/60 focus:bg-white/15"
              />
            </div>
            
            <div class="flex flex-col gap-2">
              <label for="recordsPerPage" class="font-medium text-sm">Record per pagina:</label>
              <input
                id="recordsPerPage"
                v-model.number="config.recordsPerPage"
                type="number"
                min="10"
                max="100"
                class="px-3 py-2.5 border border-white/30 rounded-md bg-white/10 text-white focus:outline-none focus:border-white/60 focus:bg-white/15"
              />
            </div>
          </div>
        </div>

        <div class="flex flex-col md:flex-row gap-4 justify-center">
          <button 
            @click="saveConfig" 
            class="px-6 py-3 bg-green-500 text-white rounded-md transition-all duration-300 hover:bg-green-600"
          >
            💾 Salva Configurazione
          </button>
          
          <button 
            @click="resetConfig" 
            class="px-6 py-3 bg-orange-500 text-white rounded-md transition-all duration-300 hover:bg-orange-600"
          >
            🔄 Reset Default
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import axios from 'axios';
import configuration from '@/config/config';

interface ImportResult {
  success: boolean
  message: string
  count?: number
}

interface Stats {
  totalRecords: number
  totalComuni: number
  totalOperatori: number
  rangeDate: string
}

interface Config {
  soapUrl: string
  saveCredentials: boolean
  refreshInterval: number
  recordsPerPage: number
}

// Reactive state
const importing = ref(false)
const loadingStats = ref(false)
const importResult = ref<ImportResult | null>(null)
const stats = ref<Stats | null>(null)

// Form data
const credentials = ref({
  username: 'sched_webservice',
  password: '%ix5QY+BVeb%ZkL',
  company: '001'
})

const dateRange = ref({
  dataInizio: '',
  dataFine: ''
})

const config = ref<Config>({
  soapUrl: 'https://hrp.serit.info/ZScheduling/servlet/SQLDataProviderServer/SERVLET/hut_export_pianific?wsdl',
  saveCredentials: false,
  refreshInterval: 30,
  recordsPerPage: 50
})

// API base URL
const API_BASE = configuration.API_BASE_URL  || 'http://localhost:3454'

// Methods
async function importData() {
  importing.value = true
  importResult.value = null

  try {
    const response = await axios.post(`${API_BASE}/api/pianificazioni/fetch`, {
      username: credentials.value.username,
      password: credentials.value.password,
      company: credentials.value.company,
      dataInizio: dateRange.value.dataInizio,
      dataFine: dateRange.value.dataFine
    }, {
      timeout: 420000 // 7 minuti
    })

    importResult.value = {
      success: true,
      message: response.data.message,
      count: response.data.count
    }

    // Ricarica le statistiche dopo un import riuscito
    setTimeout(loadStats, 1000)

  } catch (error: unknown) {
    let message = 'Errore sconosciuto'
    if (typeof error === 'object' && error !== null) {
      const err = error as { response?: { data?: { message?: string } }, message?: string }
      message = err.response?.data?.message || err.message || message
    }
    importResult.value = {
      success: false,
      message
    }
  } finally {
    importing.value = false
  }
}

async function loadStats() {
  loadingStats.value = true
  
  try {
    // Simulated stats - replace with actual API call
    await new Promise(resolve => setTimeout(resolve, 500))
    
    stats.value = {
      totalRecords: 1247,
      totalComuni: 15,
      totalOperatori: 42,
      rangeDate: '10/03/2025 - 16/03/2025'
    }
  } catch (error) {
    console.error('Error loading stats:', error)
  } finally {
    loadingStats.value = false
  }
}

function setCurrentWeek() {
  const today = new Date()
  const currentDay = today.getDay()
  
  // Calcola il lunedì della settimana corrente
  let monday = new Date(today)
  
  if (currentDay === 0) {
    // Se oggi è domenica, la "settimana corrente" è quella che inizia domani (lunedì)
    monday.setDate(today.getDate() + 1)
  } else {
    // Per tutti gli altri giorni, trova il lunedì di questa settimana
    monday.setDate(today.getDate() - (currentDay - 1))
  }
  
  monday.setHours(0, 0, 0, 0)
  
  const sunday = new Date(monday)
  sunday.setDate(monday.getDate() + 6)
  sunday.setHours(23, 59, 59, 999)

  dateRange.value.dataInizio = formatDateForInput(monday)
  dateRange.value.dataFine = formatDateForInput(sunday)
}

function formatDateForInput(date: Date): string {
  // Usa le componenti locali della data per evitare problemi di fuso orario
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function tryAgain() {
  importData()
}

async function exportData() {
  try {
    const response = await axios.get(`${API_BASE}/api/pianificazioni/export`, {
      responseType: 'blob'
    })
    
    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url
    link.download = `pianificazioni_${new Date().toISOString().split('T')[0]}.csv`
    link.click()
    window.URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Error exporting data:', error)
  }
}

function saveConfig() {
  localStorage.setItem('admin_config', JSON.stringify(config.value))
  
  if (config.value.saveCredentials) {
    localStorage.setItem('soap_credentials', JSON.stringify(credentials.value))
  } else {
    localStorage.removeItem('soap_credentials')
  }
  
  alert('Configurazione salvata!')
}

function resetConfig() {
  config.value = {
    soapUrl: 'http://your-soap-endpoint.com/service?wsdl',
    saveCredentials: false,
    refreshInterval: 30,
    recordsPerPage: 50
  }
  
  localStorage.removeItem('admin_config')
  localStorage.removeItem('soap_credentials')
  
  alert('Configurazione ripristinata ai valori predefiniti!')
}

function loadConfig() {
  const savedConfig = localStorage.getItem('admin_config')
  if (savedConfig) {
    config.value = { ...config.value, ...JSON.parse(savedConfig) }
  }
  
  if (config.value.saveCredentials) {
    const savedCredentials = localStorage.getItem('soap_credentials')
    if (savedCredentials) {
      credentials.value = { ...credentials.value, ...JSON.parse(savedCredentials) }
    }
  }
}

// Lifecycle
onMounted(() => {
  setCurrentWeek()
  loadConfig()
  loadStats()
})
</script>